import {
    type Callback,
    type Change,
    createView,
    type IStateStorage,
    isValidStateKey,
    resolveKeys,
    type StateKey,
    type StateKeysOrAll,
    type StateSubscription,
    type StateValue
} from "./state";

export const createContext = (data: IStateStorage) => {
    const subscribers: StateSubscription[] = [];
    const pendingChanges = new Map<StateKey, StateValue>();
    let pendingNotification = false;

    function onChanges(keys: StateKeysOrAll, callback: Callback): (() => void) {
        const subscription: StateSubscription = {
            keys: resolveKeys(keys),
            callback
        };
        subscribers.push(subscription);
        return () => {
            const index = subscribers.indexOf(subscription);
            if (index !== -1) {
                subscribers.splice(index, 1);
            }
        }
    }

    function notifySubscribers() {
        if (pendingChanges.size === 0) return;

        const changes: Change[] = Array.from(pendingChanges.entries()).map(([k, v]) => ({
            key: k,
            value: structuredClone(data[k] as StateValue),
            oldValue: v,
        }));

        pendingChanges.clear();

        subscribers.forEach((subscription) => {
            const relevantChanges = changes.filter(change =>
                subscription.keys.includes(change.key)
            );
            if (relevantChanges.length > 0) {
                subscription.callback(relevantChanges);
            }
        });
    }

    function scheduleNotification() {
        if (pendingNotification) return;

        pendingNotification = true;
        queueMicrotask(() => {
            notifySubscribers();
            pendingNotification = false;
        });
    }

    function updateState(newData: Partial<IStateStorage>): void {
        let hasChanges = false
        for (const key in newData) {
            if (!isValidStateKey(key)) {
                throw new Error(`Invalid state key: ${key}`);
            }
            const value: StateValue = newData[key as StateKey]!;
            const oldValue: StateValue = data[key as StateKey];
            if (oldValue === value) {
                continue; // No change, do nothing
            }
            hasChanges = true;
            (data as Record<StateKey, StateValue>)[key] = value;
            if (!pendingChanges.has(key as StateKey)) {
                pendingChanges.set(key as StateKey, structuredClone(oldValue));
            }
        }
        if (hasChanges) {
            scheduleNotification()
        }
    }

    return {
        onChanges,
        updateState,
        view: createView(data)
    }
}
